import React from 'react';

import type {
  IPainelCarteira,
  Parada,
  Parecer,
} from '../../../../../hooks/useFechamentoContabilMensal';
import { ESTEIRA, numero, PARECERES_DO_PAINEL } from '../regrasDaCarteira';

const CAIXA: React.CSSProperties = {
  minWidth: 0,
  background: '#FFFFFF',
  border: '1px solid #E4E4E7',
  borderRadius: 16,
  padding: '18px 20px',
  display: 'flex',
  flexDirection: 'column',
};

function Titulo({ titulo, sub }: { titulo: string; sub: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{titulo}</h2>
      <span style={{ fontSize: 12, color: '#52525B' }}>{sub}</span>
    </div>
  );
}

interface IPainelProps {
  pareceres: Record<Parecer, number>;
  /** "competências revisadas em" + alvo */
  alvo: string;
  parecer: Parecer | '';
  onParecer: (p: Parecer | '') => void;
  painel: IPainelCarteira | undefined;
  etapa: Parada;
  onEtapa: (e: Parada) => void;
  onAbrirEmpresa: (id: number) => void;
}

export function Painel({
  pareceres,
  alvo,
  parecer,
  onParecer,
  painel,
  etapa,
  onEtapa,
  onAbrirEmpresa,
}: IPainelProps) {
  const total = PARECERES_DO_PAINEL.reduce((s, p) => s + (pareceres[p.chave] ?? 0), 0);
  const trilha = painel?.trilha;
  const maiorEtapa = Math.max(1, ...ESTEIRA.map(e => trilha?.[e.chave] ?? 0));
  const ranque = (painel?.ranque ?? []).slice(0, 5);
  const maior = Math.max(1, ...ranque.map(r => r.criticas + r.altas));

  return (
    <div className="painel">
      <section aria-label="Pareceres do período" style={{ ...CAIXA, gap: 12 }}>
        <Titulo
          titulo="Pareceres do período"
          sub="distribuição dos pareceres no período selecionado"
        />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, letterSpacing: '-.01em' }}>
            {numero(total)}
          </span>
          <span style={{ fontSize: 12.5, color: '#52525B' }}>
            competências revisadas em <b style={{ color: '#0F172A' }}>{alvo}</b>
          </span>
        </div>
        <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', gap: 2 }}>
          {PARECERES_DO_PAINEL.map(p => {
            const q = pareceres[p.chave] ?? 0;
            if (!q) return null;
            return (
              <button
                type="button"
                key={p.chave}
                className="seg"
                aria-label={p.nome}
                onClick={() => onParecer(parecer === p.chave ? '' : p.chave)}
                style={{
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  flex: `${q} 1 0`,
                  background: p.cor,
                  opacity: parecer && parecer !== p.chave ? 0.3 : 1,
                }}
              />
            );
          })}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {PARECERES_DO_PAINEL.map(p => {
            const q = pareceres[p.chave] ?? 0;
            const ativo = parecer === p.chave;
            return (
              <button
                type="button"
                key={p.chave}
                className="linhaInd"
                onClick={() => onParecer(ativo ? '' : p.chave)}
                aria-pressed={ativo}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: 'calc(100% + 16px)',
                  margin: '0 -8px',
                  boxSizing: 'border-box',
                  padding: '6px 8px',
                  borderRadius: 8,
                  border: `1px solid ${ativo ? p.cor : 'transparent'}`,
                  background: ativo ? p.suave : '#FFFFFF',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 12.5,
                  color: '#0F172A',
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    flexShrink: 0,
                    background: p.cor,
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {p.nome}
                </span>
                <span style={{ fontWeight: 700 }}>{numero(q)}</span>
                <span
                  style={{ minWidth: 44, textAlign: 'right', fontSize: 11.5, color: '#52525B' }}
                >
                  {(total ? (q / total) * 100 : 0).toLocaleString('pt-BR', {
                    maximumFractionDigits: 1,
                  })}
                  %
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section aria-label="Onde as empresas pararam" style={{ ...CAIXA, gap: 14 }}>
        <Titulo
          titulo="Onde as empresas pararam"
          sub={`etapa em que cada uma das ${numero(painel?.total ?? 0)} empresas está hoje`}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          {ESTEIRA.map(e => {
            const q = trilha?.[e.chave] ?? 0;
            const ativo = etapa === e.chave;
            return (
              <button
                type="button"
                key={e.chave}
                onClick={() => onEtapa(ativo ? '' : (e.chave as Parada))}
                aria-pressed={ativo}
                title="Clique para ver só as empresas paradas nesta etapa"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 118px) minmax(0, 1fr) 72px',
                  alignItems: 'center',
                  gap: 10,
                  border: 0,
                  padding: '2px 4px',
                  margin: '-2px -4px',
                  borderRadius: 8,
                  background: ativo ? '#E9F4FD' : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: '#0F172A',
                }}
              >
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '.05em',
                      textTransform: 'uppercase',
                      color: '#64748B',
                    }}
                  >
                    {e.passo}
                  </span>
                  <span
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {e.nome}
                  </span>
                </span>
                <span
                  style={{ height: 10, borderRadius: 5, background: '#EEF2F6', overflow: 'hidden' }}
                >
                  <span
                    style={{
                      display: 'block',
                      height: 10,
                      borderRadius: 5,
                      width: `${Math.round((q / maiorEtapa) * 100)}%`,
                      background: e.cor,
                    }}
                  />
                </span>
                <span style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <b style={{ fontSize: 15 }}>{numero(q)}</b>{' '}
                  <span style={{ fontSize: 11, color: '#64748B' }}>
                    {painel?.total ? Math.round((q / painel.total) * 100) : 0}%
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section aria-label="Mais pontos críticos" style={{ ...CAIXA, gap: 14 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <Titulo titulo="Mais pontos críticos" sub="críticas e altas no período" />
          <div style={{ display: 'flex', gap: 10, fontSize: 11, color: '#52525B', paddingTop: 2 }}>
            {(
              [
                ['#DC2626', 'críticas'],
                ['#D97706', 'altas'],
              ] as const
            ).map(([cor, rotulo]) => (
              <span key={rotulo} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: cor }} />
                {rotulo}
              </span>
            ))}
          </div>
        </div>
        {ranque.length === 0 && (
          <span style={{ fontSize: 12.5, color: '#52525B' }}>Nenhum ponto crítico ou alto.</span>
        )}
        <ol
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 11,
          }}
        >
          {ranque.map((r, k) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onAbrirEmpresa(r.id)}
                title="Abrir a empresa"
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 5,
                  border: 0,
                  padding: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: '#0F172A',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 12.5,
                    width: '100%',
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      flexShrink: 0,
                      borderRadius: 999,
                      background: '#F1F5F9',
                      color: '#334155',
                      fontSize: 10.5,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {k + 1}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.razao}
                  </span>
                  <span style={{ whiteSpace: 'nowrap', fontSize: 11.5, color: '#52525B' }}>
                    <b style={{ color: '#B91C1C' }}>{r.criticas}</b> crít. ·{' '}
                    <b style={{ color: '#B45309' }}>{r.altas}</b> altas
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    height: 6,
                    marginLeft: 26,
                    width: 'calc(100% - 26px)',
                    borderRadius: 3,
                    overflow: 'hidden',
                    gap: 1,
                    background: '#EEF2F6',
                  }}
                >
                  <span
                    style={{ width: `${(r.criticas / maior) * 100}%`, background: '#DC2626' }}
                  />
                  <span style={{ width: `${(r.altas / maior) * 100}%`, background: '#D97706' }} />
                </div>
              </button>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
